import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getStats, getInvoices, seedData, money, baseMoney, STATUS_META } from "@/lib/apiClient";
import { ConfidenceBadge } from "@/components/ConfidenceBadge";
import { SpendCharts } from "@/components/SpendCharts";
import { DueAlerts } from "@/components/DueAlerts";
import { GmailHealthBanner } from "@/components/GmailHealthBanner";
import { WelcomeChecklist } from "@/components/WelcomeChecklist";
import { InboundChannels } from "@/components/InboundChannels";
import { FxExposure } from "@/components/FxExposure";
import { ReviewSla } from "@/components/ReviewSla";
import { ClosePackDialog } from "@/components/ClosePackDialog";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Clock,
  Sparkles,
  AlertTriangle,
  Timer,
  UploadCloud,
  ArrowRight,
  FileText,
  Database,
  UserCheck,
  Banknote,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const KpiCard = ({ icon: Icon, label, value, sub, tone, testId, delay, onClick, className }) => (
  <div
    data-testid={testId}
    onClick={onClick}
    className={cn(
      "animate-fade-up min-w-0 rounded-xl border border-border bg-card p-5 hover:shadow-lg hover:shadow-primary/5 transition-shadow",
      onClick && "cursor-pointer hover:border-primary/50",
      className
    )}
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="flex items-center justify-between">
      <span className={cn("flex h-10 w-10 items-center justify-center rounded-lg", tone)}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <div className={cn("mt-4 font-mono font-bold tracking-tight truncate leading-tight", String(value).length > 9 ? "text-xl lg:text-2xl xl:text-xl" : "text-3xl")} title={String(value)}>{value}</div>
    <div className="mt-1 text-sm font-medium">{label}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [due, setDue] = useState(null);
  const [dueKey, setDueKey] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useApp();

  const load = useCallback(async () => {
    const [s, inv] = await Promise.all([getStats(user), getInvoices({ limit: 8 })]);
    setStats(s);
    setInvoices(inv);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSeed = async () => {
    const res = await seedData();
    if (res.seeded) toast.success(`Loaded ${res.count} sample invoices`);
    else toast.info("Sample data already loaded");
    load();
  };

  const empty = !loading && invoices.length === 0;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            AI-coded supplier invoices, ready for your review and approval.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ClosePackDialog />
          <Button
            data-testid="dashboard-seed-btn"
            variant="outline"
            onClick={handleSeed}
          >
            <Database className="h-4 w-4 mr-2" /> Load Samples
          </Button>
          <Button data-testid="dashboard-upload-btn" onClick={() => navigate("/upload")}>
            <UploadCloud className="h-4 w-4 mr-2" /> Upload Invoice
          </Button>
        </div>
      </div>

      <GmailHealthBanner />

      <WelcomeChecklist />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          <KpiCard
            testId="kpi-paid"
            icon={Banknote}
            label="Paid This Month"
            value={due ? money(due.paid_this_month_value, due.base_currency) : "—"}
            sub={due ? `${due.paid_this_month_count} invoice(s) · ${money(due.paid_value, due.base_currency)} all time` : "Cash out"}
            tone="bg-emerald-500/15 text-emerald-500"
            delay={0}
          />
          <KpiCard
            testId="kpi-assigned"
            icon={UserCheck}
            label="Assigned to You"
            value={stats.assigned_to_me}
            sub={stats.assigned_to_me ? "Teammates are waiting on you" : "Nothing handed to you"}
            tone="bg-primary/15 text-primary"
            delay={20}
            onClick={() => navigate("/review?mine=1")}
          />
          <KpiCard
            testId="kpi-pending"
            icon={Clock}
            label="Pending Review"
            value={stats.pending_review}
            sub={stats.pending_review ? "Tap to approve one by one" : "Awaiting human decision"}
            tone="bg-amber-500/15 text-amber-500"
            delay={40}
            onClick={() => navigate("/approve")}
          />
          <KpiCard
            testId="kpi-autocoded"
            icon={Sparkles}
            label="Auto-Coded"
            value={stats.auto_coded}
            sub={`${money(stats.approved_value, stats.base_currency)} approved${stats.foreign_count ? ` · ${stats.foreign_count} foreign-currency` : ""}`}
            tone="bg-indigo-500/15 text-indigo-400"
            delay={60}
          />
          <KpiCard
            testId="kpi-exception"
            icon={AlertTriangle}
            label="Exception Rate"
            value={`${stats.exception_rate}%`}
            sub={`${stats.flagged} flagged < 80%`}
            tone="bg-red-500/15 text-red-500"
            delay={120}
          />
          <KpiCard
            testId="kpi-timesaved"
            icon={Timer}
            label="Time Saved"
            value={`${stats.time_saved_hours}h`}
            sub={`${stats.line_items_coded} lines coded`}
            tone="bg-emerald-500/15 text-emerald-500"
            delay={180}
          />
          <KpiCard
            testId="kpi-duplicates-stopped"
            icon={ShieldCheck}
            label="Not Paid Twice"
            value={money(stats.duplicates_stopped?.value || 0, stats.base_currency)}
            sub={stats.duplicates_stopped?.count
              ? `${stats.duplicates_stopped.count} duplicate${stats.duplicates_stopped.count === 1 ? "" : "s"} stopped (${stats.duplicates_stopped.auto} by rule)${stats.duplicates_stopped.month_count ? ` · ${money(stats.duplicates_stopped.month_value, stats.base_currency)} this month` : ""}${stats.duplicates_stopped.waiting ? ` · ${stats.duplicates_stopped.waiting} waiting` : ""}`
              : stats.duplicates_stopped?.waiting ? `${stats.duplicates_stopped.waiting} possible duplicate(s) waiting for a decision` : "No duplicates caught yet"}
            tone="bg-teal-500/15 text-teal-600 dark:text-teal-400"
            className="col-span-2 lg:col-span-1"
            delay={210}
            onClick={() => navigate(stats.duplicates_stopped?.waiting ? "/review?filter=duplicates" : "/review?filter=rejected")}
          />
        </div>
      )}

      {!empty && <InboundChannels channels={stats?.channels_this_month} />}

      {!empty && <FxExposure />}

      {!empty && <DueAlerts key={dueKey} onLoaded={setDue} />}

      {!empty && <ReviewSla due={due} onChanged={() => setDueKey((k) => k + 1)} />}

      {!empty && <SpendCharts />}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl font-semibold">Recent Invoices</h2>
          <button
            data-testid="view-review-queue-link"
            onClick={() => navigate("/review")}
            className="text-sm text-primary flex items-center gap-1 hover:gap-2 transition-all"
          >
            Review Queue <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {empty ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center">
            <FileText className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <p className="mt-3 font-medium">No invoices yet</p>
            <p className="text-sm text-muted-foreground">Upload one or load the sample dataset to explore.</p>
            <div className="mt-4 flex justify-center gap-2">
              <Button data-testid="empty-seed-btn" variant="outline" onClick={handleSeed}>
                <Database className="h-4 w-4 mr-2" /> Load Samples
              </Button>
              <Button data-testid="empty-upload-btn" onClick={() => navigate("/upload")}>
                <UploadCloud className="h-4 w-4 mr-2" /> Upload
              </Button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="divide-y divide-border">
              {invoices.slice(0, 8).map((inv) => {
                const meta = STATUS_META[inv.status];
                return (
                  <button
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    onClick={() => navigate(`/invoice/${inv.id}`)}
                    className="w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left hover:bg-muted/50 transition-colors"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                      {inv.source === "pdf" ? <FileText className="h-5 w-5" /> : <UploadCloud className="h-5 w-5" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{inv.supplier || "Unknown supplier"}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {inv.invoice_number || "—"} · {inv.invoice_date || "no date"}
                      </div>
                    </div>
                    <div className="hidden sm:block font-mono text-sm font-semibold">
                      {money(inv.total, inv.currency)}
                      {baseMoney(inv) && <span className="block text-[11px] text-muted-foreground">≈ {baseMoney(inv)}</span>}
                    </div>
                    <ConfidenceBadge value={inv.min_confidence} showLabel={false} />
                    <span
                      className={cn(
                        "hidden md:inline-flex rounded-lg border px-2.5 py-1 text-xs font-medium whitespace-nowrap",
                        meta.cls
                      )}
                    >
                      {meta.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
